import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuildingColumns,
  faCalendar,
  faCircleInfo,
  faFileInvoice,
  faLock,
  faSliders,
  faTag,
  faTimes,
  faUnlock,
} from '@fortawesome/free-solid-svg-icons';
import Button from 'components/ui/Button/Button';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import FormGroupDatepicker from 'components/ui/FormGroupDatepicker/FormGroupDatepicker';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import useModalClass from 'hooks/useModalClass';
import { createPortal } from 'react-dom';
import { dateFormat, showError, showSuccess } from 'utils/utils';
import { MyOrder } from 'ts/shared/interfaces/MOResponse';
import { changeSegmentEditor, lockPnr, LockPnrParams } from 'services/myOrders';
import { getStatus } from 'constants/mo';
import ScreenshotSupport from '../../ScreenshotSupport';
import MaximizeModal from '../../MaximizeModal';
import withErrorBoundary from '../../withErrorBoundary';
import Tabs from 'components/ui/Tabs/Tabs';
import { formatISO } from 'date-fns';

interface MoSEMainProps {
  myOrder?: MyOrder;
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
  onSubmit: (myOrder: MyOrder) => void;
}

const MoSEMain: FC<MoSEMainProps> = ({
  myOrder,
  openModal,
  setOpenModal,
  ...props
}) => {
  const [t] = useTranslation();
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const classList = useModalClass(openModal);
  const [activeTab, setActiveTab] = useState(0);
  const [orderModel, setOrderModel] = useState<any>(myOrder?.order || null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPnrLockLoading, setIsPnrLockLoading] = useState(false);
  const segment = orderModel?.segments.find((s: any) => s.orderSegId === myOrder?.fullSegment.orderSegId);
  const isPnrLocked = (segment?.suppPnr || '').includes('BPRLOCK:');
  const toIsoString = (value: Date) => formatISO(value).replace(/([+-]\d{2}:\d{2})$/, 'Z');
  const toValidDate = (value?: string) => {
    const date = value ? new Date(value) : new Date();
    return Number.isNaN(date.getTime()) ? new Date() : date;
  };
  const getNumberOfNights = (checkIn: Date, checkOut: Date) => Math.max(
    0,
    Math.ceil(
      (new Date(checkOut).setHours(0, 0, 0, 0) - new Date(checkIn).setHours(0, 0, 0, 0)) / 86400000
    )
  );
  const supplierOptions = Object.values(myOrder?.suppliersByCode || {}).map((supplier: any) => ({
    value: supplier.suppCode,
    label: `${supplier.suppDesc} (${supplier.suppCode})`,
  }));

  useEffect(() => {
    if (openModal && myOrder) {
      setOrderModel(myOrder.order);
      setActiveTab(0);
    }
  }, [myOrder, openModal]);

  const onChangeSegment = (updater: (segmentValue: any) => any) => {
    setOrderModel((prevOrder: any) => {
      if (!prevOrder || !myOrder) return prevOrder;
      return {
        ...prevOrder,
        segments: prevOrder.segments.map((s: any) => (
          s.orderSegId === myOrder.fullSegment.orderSegId ? updater(s) : s
        )),
      };
    });
  };

  const onClickSave = async () => {
    if (!myOrder || !orderModel || !segment) return;
    setIsLoading(true);
    const result = await changeSegmentEditor({
      order: orderModel,
      changePrice: true,
    });
    if (result) {
      showSuccess(t('mo.segment_gross_edited'));
      props.onSubmit({ ...result, orderRow: { ...result.orderRow, status: myOrder.orderRow.status } });
    } else {
      showError(t('something_went_wrong'));
    }
    setIsLoading(false);
  };

  const onClickTogglePnrLock = async () => {
    if (!myOrder || !orderModel || !segment) return;
    const params: LockPnrParams = {
      orderId: myOrder.orderRow.orderId,
      segmentId: myOrder.orderRow.segmentId,
      lockPnr: !isPnrLocked,
    };
    setIsPnrLockLoading(true);
    const response = await lockPnr(params);

    if (response && !response.hasErrors) {
      showSuccess(t(isPnrLocked ? 'mo.unlocked' : 'mo.locked'));
      if (response.myOrder?.order) {
        setOrderModel(response.myOrder.order);
      }
      props.onSubmit(response.myOrder);
    } else if (response?.hasErrors) {
      showError(response.errorMessage || t('something_went_wrong'));
    } else {
      showError(t('something_went_wrong'));
    }

    setIsPnrLockLoading(false);
  };

  return createPortal(
    <div
      className="modal-overlay modal-overlay-top mo-message-modal-overlay modal-maximizable" style={{ direction: 'ltr' }}
      ref={modalOverlayRef}
    >
      <div className={classList + ' modal'}>
        <div className="modal-title">
          <h5 className="title">
            Segment Editor{' '}
            {myOrder?.fullSegment && (
              <Fragment>
                {' '}-{' '}Order: {myOrder.orderRow.orderId}{' '}
                Segment:
                {myOrder.fullSegment.orderSegId}{' '}
                <span className={`mo-status ${getStatus(myOrder.orderRow.status)}`}>
                  {myOrder.orderRow.status}
                </span>
              </Fragment>
            )}
          </h5>
          <div>
            <ScreenshotSupport />
            <MaximizeModal modalOverlayRef={modalOverlayRef} />
            <FontAwesomeIcon icon={faTimes} onClick={() => setOpenModal(false)} />
          </div>
        </div>
        <div className="modal-body modal-body-overflow">
          <Tabs
        size="small"
            label="Segment editor"
            value={String(activeTab)}
            onValueChange={(v) => setActiveTab(Number(v))}
            className="mb-10 room-tabs"
            items={[
              {
                value: '0',
                label: (
                  <>
                    <FontAwesomeIcon icon={faCircleInfo} className="mr-5" />
                    Order Details
                  </>
                ),
              },
              {
                value: '1',
                label: (
                  <>
                    <FontAwesomeIcon icon={faFileInvoice} className="mr-5" />
                    Voucher
                  </>
                ),
              },
              {
                value: '2',
                label: (
                  <>
                    <FontAwesomeIcon icon={faTag} className="mr-5" />
                    Supplier
                  </>
                ),
              },
              {
                value: '3',
                label: (
                  <>
                    <FontAwesomeIcon icon={faBuildingColumns} className="mr-5" />
                    City
                  </>
                ),
              },
              {
                value: '4',
                label: (
                  <>
                    <FontAwesomeIcon icon={faSliders} className="mr-5" />
                    Params
                  </>
                ),
              },
            ]}
          />

          {activeTab === 0 && (
            <div className="mo-block-content">
              <div className="mo-block mb-20">
                <div className="mo-block-title">
                  <span>Order Dates</span>
                </div>
                <div className="mo-block-content">
                  <div className="row">
                    <div className="col-md-4 col-12 mb-15">
                      <FormGroupDatepicker
                        onChange={(value) => onChangeSegment((seg) => ({
                          ...seg,
                          checkIn: toIsoString(value),
                          numberOfNights: getNumberOfNights(value, toValidDate(seg?.checkOut)),
                        }))}
                        value={new Date(segment?.checkIn || '')}
                        label={'Check In'}
                        movablePlaceholder={'Check In'}
                        movablePlaceholderIcon={faCalendar}
                        maxDate={new Date(segment?.checkOut || '')}
                      />
                    </div>
                    <div className="col-md-4 col-12 mb-15">
                      <FormGroupDatepicker
                        onChange={(value) => onChangeSegment((seg) => ({
                          ...seg,
                          checkOut: toIsoString(value),
                          numberOfNights: getNumberOfNights(toValidDate(seg?.checkIn), value),
                        }))}
                        value={new Date(segment?.checkOut || '')}
                        label={'Check Out'}
                        movablePlaceholder={'Check Out'}
                        movablePlaceholderIcon={faCalendar}
                        minDate={new Date(segment?.checkIn || '')}
                      />
                    </div>
                    <div className="col-md-2 col-6 mb-15">
                      <FormGroup
                        onChange={() => { }}
                        value={segment?.numberOfNights?.toString() || ''}
                        label={'Nights'}
                        movablePlaceholder={'Nights'}
                        allowOnlyNumbers
                        disabled={true}
                      />
                    </div>
                    <div className="col-md-2 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, numberOfPaxs: Number(value) || 0 }))}
                        value={segment?.numberOfPaxs?.toString() || ''}
                        label={'Paxes'}
                        movablePlaceholder={'Paxes'}
                        allowOnlyNumbers
                      />
                    </div>
                    <div className="col-md-4 col-12 mb-15">
                      <FormGroupDatepicker
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, departureDate: toIsoString(value) }))}
                        value={new Date(segment?.departureDate || '')}
                        label={'Departure Date'}
                        movablePlaceholder={'Departure Date'}
                        movablePlaceholderIcon={faCalendar}
                      />
                    </div>
                    <div className="col-md-8 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => setOrderModel((prev: any) => ({ ...prev, param_5: value }))}
                        value={orderModel?.param_5 || ''}
                        label={'Param 5'}
                        movablePlaceholder={'Param 5'}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mo-block mb-20">
                <div className="mo-block-title">
                  <span>Hotel Info</span>
                </div>
                <div className="mo-block-content">
                  <div className="row">
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, itemDesc: value }))}
                        value={segment?.itemDesc || ''}
                        label={'Hotel Desc'}
                        movablePlaceholder={'Hotel Desc'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, itemAddress: value }))}
                        value={segment?.itemAddress || ''}
                        label={'Hotel Address'}
                        movablePlaceholder={'Hotel Address'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, itemPhone: value }))}
                        value={segment?.itemPhone || ''}
                        label={'Hotel Phone'}
                        movablePlaceholder={'Hotel Phone'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, itemFax: value }))}
                        value={segment?.itemFax || ''}
                        label={'Hotel Fax'}
                        movablePlaceholder={'Hotel Fax'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, itemZip: value }))}
                        value={segment?.itemZip || ''}
                        label={'Hotel Zip'}
                        movablePlaceholder={'Hotel Zip'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, itemLocationInfo: value }))}
                        value={segment?.itemLocationInfo || ''}
                        label={'Hotel Location Info'}
                        movablePlaceholder={'Hotel Location Info'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, itemThumb: value }))}
                        value={segment?.itemThumb || ''}
                        label={'Hotel Thumb'}
                        movablePlaceholder={'Hotel Thumb'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, itemMap: value }))}
                        value={segment?.itemMap || ''}
                        label={'Hotel Map'}
                        movablePlaceholder={'Hotel Map'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, itemGeoCodes: value }))}
                        value={segment?.itemGeoCodes || ''}
                        label={'Hotel Geo Codes'}
                        movablePlaceholder={'Hotel Geo Codes'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, itemCategoryDesc: value }))}
                        value={segment?.itemCategoryDesc || ''}
                        label={'Hotel Category Desc'}
                        movablePlaceholder={'Hotel Category Desc'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, itemStarRateCode: value }))}
                        value={segment?.itemStarRateCode || ''}
                        label={'Hotel Star Rate Code'}
                        movablePlaceholder={'Hotel Star Rate Code'}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mo-block">
                <div className="mo-block-title">
                  <span>Info</span>
                </div>
                <div className="mo-block-content">
                  <div className="row">
                    <div className="col-md-4 col-12 mb-15">
                      <FormGroup
                        onChange={() => { }}
                        value={segment?.dateC ? dateFormat(segment.dateC, 'dd/MM/yy HH:mm') : ''}
                        label={'Date C'}
                        movablePlaceholder={'Date C'}
                        disabled={true}
                      />
                    </div>
                    <div className="col-md-4 col-12 mb-15">
                      <FormGroup
                        onChange={() => { }}
                        value={segment?.user_f_name || ''}
                        label={'User_f_name'}
                        movablePlaceholder={'User_f_name'}
                        disabled={true}
                      />
                    </div>
                    <div className="col-md-4 col-12 mb-15">
                      <FormGroup
                        onChange={() => { }}
                        value={segment?.user_l_name || ''}
                        label={'User_l_name'}
                        movablePlaceholder={'User_l_name'}
                        disabled={true}
                      />
                    </div>
                    <div className="col-md-4 col-12 mb-15">
                      <FormGroup
                        onChange={() => { }}
                        value={segment?.dateU ? dateFormat(segment.dateU, 'dd/MM/yy HH:mm') : ''}
                        label={'Date U'}
                        movablePlaceholder={'Date U'}
                        disabled={true}
                      />
                    </div>
                    <div className="col-md-4 col-12 mb-15">
                      <FormGroup
                        onChange={() => { }}
                        value={segment?.user_f_name1 || ''}
                        label={'User_f_name1'}
                        movablePlaceholder={'User_f_name1'}
                        disabled={true}
                      />
                    </div>
                    <div className="col-md-4 col-12 mb-15">
                      <FormGroup
                        onChange={() => { }}
                        value={segment?.user_l_name1 || ''}
                        label={'User_l_name1'}
                        movablePlaceholder={'User_l_name1'}
                        disabled={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 1 && (
            <div className="mo-block-content">
              <div className="mo-block mb-20">
                <div className="mo-block-title">
                  <span>Supplier Voucher</span>
                </div>
                <div className="mo-block-content">
                  <div className="row">
                    <div className="col-12 mb-15">
                      <div className="form-group mb-0">
                        <label>Note</label>
                        <textarea
                          className="form-control form-control-textarea"
                          value={segment?.suppNote || ''}
                          rows={4}
                          onChange={(e) => onChangeSegment((seg) => ({ ...seg, suppNote: e.target.value }))}
                        ></textarea>
                      </div>
                    </div>
                    <div className="col-md-8 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, suppVoucherCode: value }))}
                        value={segment?.suppVoucherCode || ''}
                        label={'Code'}
                        movablePlaceholder={'Code'}
                      />
                    </div>
                    <div className="col-md-4 col-12 mb-15">
                      <div className="form-group mb-0">
                        <Checkbox
                          label={'Display'}
                          checked={!!segment?.suppVoucherDisplay}
                          onChange={(value) => onChangeSegment((seg) => ({ ...seg, suppVoucherDisplay: value }))}
                        />
                      </div>
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroupDatepicker
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, suppVoucherDate: toIsoString(value) }))}
                        value={new Date(segment?.suppVoucherDate)}
                        label={'Date'}
                        movablePlaceholder={'Date'}
                        movablePlaceholderIcon={faCalendar}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, suppVoucherLink: value }))}
                        value={segment?.suppVoucherLink || ''}
                        label={'Link'}
                        movablePlaceholder={'Link'}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mo-block">
                <div className="mo-block-title">
                  <span>Paid By Supplier</span>
                </div>
                <div className="mo-block-content">
                  <div className="row">
                    <div className="col-12 mb-15">
                      <div className="form-group mb-0">
                        <label>Note</label>
                        <textarea
                          className="form-control form-control-textarea"
                          value={segment?.paidBySuppNote || ''}
                          rows={4}
                          onChange={(e) => onChangeSegment((seg) => ({ ...seg, paidBySuppNote: e.target.value }))}
                        ></textarea>
                      </div>
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, paidBySuppDesc: value }))}
                        value={segment?.paidBySuppDesc || ''}
                        label={'Desc'}
                        movablePlaceholder={'Desc'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, paidBySuppDesc2: value }))}
                        value={segment?.paidBySuppDesc2 || ''}
                        label={'Desc 2'}
                        movablePlaceholder={'Desc 2'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, paidBySuppAddress: value }))}
                        value={segment?.paidBySuppAddress || ''}
                        label={'Address'}
                        movablePlaceholder={'Address'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, paidBySuppAddress2: value }))}
                        value={segment?.paidBySuppAddress2 || ''}
                        label={'Address 2'}
                        movablePlaceholder={'Address 2'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, paidBySuppPhone: value }))}
                        value={segment?.paidBySuppPhone || ''}
                        label={'Phone'}
                        movablePlaceholder={'Phone'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, paidBySuppFax: value }))}
                        value={segment?.paidBySuppFax || ''}
                        label={'Fax'}
                        movablePlaceholder={'Fax'}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 2 && (
            <div className="mo-block-content">
              <div className="mo-block mb-20">
                <div className="mo-block-title">
                  <span>Supplier</span>
                </div>
                <div className="mo-block-content">
                  <div className="row">
                    <div className="col-12 mb-15">
                      <SelectCustom
                        onChange={(value) => {
                          const selected = Object.values(myOrder?.suppliersByCode || {}).find((supplier: any) => supplier.suppCode === value);
                          onChangeSegment((seg) => ({
                            ...seg,
                            sysSuppCode: value,
                            sysSuppDesc: selected?.suppDesc || seg.sysSuppDesc,
                          }));
                        }}
                        value={segment?.sysSuppCode || ''}
                        options={supplierOptions}
                        movablePlaceholder={'Supplier'}
                      />
                    </div>
                    <div className="col-md-8 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, suppPnr: value }))}
                        value={segment?.suppPnr || ''}
                        label={'PNR'}
                        movablePlaceholder={'PNR'}
                      />
                    </div>
                    <div className="col-md-4 col-12 mb-15">
                      <Button
                        title={t(isPnrLocked ? 'mo.unlock_pnr' : 'mo.lock_pnr')}
                        className="btn btn-primary"
                        icon={isPnrLocked ? faUnlock : faLock}
                        iconclassname="mr-5"
                        disabled={isLoading || isPnrLockLoading}
                        onClick={onClickTogglePnrLock}
                      />
                    </div>
                    <div className="col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, suppPnrSeg: value }))}
                        value={segment?.suppPnrSeg || ''}
                        label={'PNR Seg'}
                        movablePlaceholder={'PNR Seg'}
                      />
                    </div>
                    <div className="col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, suppPnrToken: value }))}
                        value={segment?.suppPnrToken || ''}
                        label={'PNR Token'}
                        movablePlaceholder={'PNR Token'}
                      />
                    </div>
                    <div className="col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, suppPnrSegConRef: value }))}
                        value={segment?.suppPnrSegConRef || ''}
                        label={'PNR Seg Con Ref'}
                        movablePlaceholder={'PNR Seg Con Ref'}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mo-block">
                <div className="mo-block-title">
                  <span>Backoffice</span>
                </div>
                <div className="mo-block-content">
                  <div className="row">
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, backOfficeId: value }))}
                        value={segment?.backOfficeId || ''}
                        label={'Backoffice Id'}
                        movablePlaceholder={'Backoffice Id'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, backOfficeExchangeVoucher: value }))}
                        value={segment?.backOfficeExchangeVoucher || ''}
                        label={'Exchange Voucher'}
                        movablePlaceholder={'Exchange Voucher'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, backOfficeSuppCode: value }))}
                        value={segment?.backOfficeSuppCode || ''}
                        label={'Supplier Code'}
                        movablePlaceholder={'Supplier Code'}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 3 && (
            <div className="mo-block-content">
              <div className="mo-block">
                <div className="mo-block-title">
                  <span>City Details</span>
                </div>
                <div className="mo-block-content">
                  <div className="row">
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, suppCityCode: value }))}
                        value={segment?.suppCityCode || ''}
                        label={'Supp City Code'}
                        movablePlaceholder={'Supp City Code'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, suppCityDesc: value }))}
                        value={segment?.suppCityDesc || ''}
                        label={'Supp City Desc'}
                        movablePlaceholder={'Supp City Desc'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, sysCityCode: value }))}
                        value={segment?.sysCityCode || ''}
                        label={'Sys City Code'}
                        movablePlaceholder={'Sys City Code'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, sysCityDesc: value }))}
                        value={segment?.sysCityDesc || ''}
                        label={'Sys City Desc'}
                        movablePlaceholder={'Sys City Desc'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSegment((seg) => ({ ...seg, countryCode: value }))}
                        value={segment?.countryCode || ''}
                        label={'Country Code'}
                        movablePlaceholder={'Country Code'}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 4 && (
            <div className="mo-block-content">
              <div className="mo-block">
                <div className="mo-block-title">
                  <span>Params</span>
                </div>
                <div className="mo-block-content">
                  <div className="row">
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => setOrderModel((prev: any) => ({ ...prev, param_1: value }))}
                        value={orderModel?.param_1 || ''}
                        label={'Param_1'}
                        movablePlaceholder={'Param_1'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => setOrderModel((prev: any) => ({ ...prev, param_2: value }))}
                        value={orderModel?.param_2 || ''}
                        label={'Param_2'}
                        movablePlaceholder={'Param_2'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => setOrderModel((prev: any) => ({ ...prev, param_3: value }))}
                        value={orderModel?.param_3 || ''}
                        label={'Param_3'}
                        movablePlaceholder={'Param_3'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => setOrderModel((prev: any) => ({ ...prev, param_4: value }))}
                        value={orderModel?.param_4 || ''}
                        label={'Param_4'}
                        movablePlaceholder={'Param_4'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => setOrderModel((prev: any) => ({ ...prev, param_5: value }))}
                        value={orderModel?.param_5 || ''}
                        label={'Param_5'}
                        movablePlaceholder={'Param_5'}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <Button disabled={isLoading} title={t('save')} className="btn btn-primary ml-0 mr-a" onClick={onClickSave} />
          <Button
            title={t('cancel')}
            className="btn btn-default"
            onClick={() => setOpenModal(false)}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default withErrorBoundary(MoSEMain, 'MoSEMain');